import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Clock } from 'lucide-react';
import { getFechasPorIngresar, type FechaPorIngresar } from '@/shared/api/controllers/inventario';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { cn } from '@/shared/lib/cn';
import { ddmmyyyy } from './Garantia';

/**
 * Ingreso estimado de las líneas que dependen de stock por ingresar. Es
 * informativo: el vendedor elige una de las fechas probables de las
 * importaciones que traen el artículo y queda anotada en la cotización, pero
 * no es un compromiso de entrega y nunca bloquea el envío.
 */

/** Fechas por artículo; si falla, el picker cae a "Sin fecha estimada". */
export function useFechasPorIngresar(enabled = true) {
  const { config } = useActiveCountry();
  return useQuery({
    queryKey: ['ventas', config.code, 'fechas-por-ingresar'],
    queryFn: getFechasPorIngresar,
    staleTime: 60_000,
    refetchOnMount: 'always',
    enabled,
  });
}

export function PorIngresarBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700"
      title="Parte de la cantidad depende de stock que todavía no ingresó"
    >
      <Clock className="h-3 w-3" aria-hidden /> Por ingresar
    </span>
  );
}

export function FechaIngresoPicker({
  opciones,
  value,
  onChange,
}: {
  /** Importaciones del artículo (undefined = ninguna: stock de compras nacionales). */
  opciones: FechaPorIngresar[] | undefined;
  /** yyyy-mm-dd elegida, o null. */
  value: string | null;
  onChange: (fecha: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const conFecha = (opciones ?? []).filter((o) => o.fechaProbable);
  if (conFecha.length === 0 && !value) {
    return <span className="text-[11px] text-sky-700">Sin fecha estimada</span>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 hover:bg-sky-100',
          )}
        >
          <Clock className="h-3 w-3" aria-hidden />
          {value ? `Ingreso estimado: ${ddmmyyyy(value)}` : 'Elegir fecha de ingreso'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 gap-1">
        <p className="px-1 pb-1 text-xs font-semibold text-foreground">Fechas estimadas de ingreso</p>
        {conFecha.map((o) => {
          const fecha = o.fechaProbable!;
          const elegida = fecha === value;
          return (
            <button
              key={o.importacionId}
              type="button"
              onClick={() => {
                onChange(fecha);
                setOpen(false);
              }}
              className={cn(
                'flex items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-sky-50',
                elegida && 'bg-sky-50',
              )}
            >
              <Check className={cn('mt-0.5 h-4 w-4 shrink-0 text-sky-700', !elegida && 'invisible')} aria-hidden />
              <span className="min-w-0">
                <span className="block font-medium">
                  {ddmmyyyy(fecha)} · Imp. N° {o.numero}
                </span>
                {o.descripcion && (
                  <span className="block truncate text-xs text-muted-foreground">{o.descripcion}</span>
                )}
              </span>
            </button>
          );
        })}
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
          >
            Quitar fecha
          </button>
        )}
        <p className="px-1 pt-1 text-[11px] text-muted-foreground">
          Estimación informativa, no es un compromiso de entrega.
        </p>
      </PopoverContent>
    </Popover>
  );
}
